import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  PipelineOpportunity,
  REVENUE_REGULARITIES,
  PIPELINE_STAGES,
  OUTREACH_STATUSES,
  PIPELINE_COLUMNS,
} from '@/types/pipeline';
import type { RevenueRegularity } from '@/types/pipeline';

// Re-export types and constants
export type { PipelineOpportunity, RevenueRegularity } from '@/types/pipeline';
export { OUTREACH_STATUSES, PIPELINE_COLUMNS, PIPELINE_STAGES, REVENUE_REGULARITIES } from '@/types/pipeline';

// ============= TYPES =============

export interface PipelineStage {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  probability: number;
  is_won: boolean;
  is_lost: boolean;
  color: string;
  is_active: boolean;
}

export interface PipelineProduct {
  id: string;
  name: string;
  base_price: number | null;
  price_type: string;
  duration_months: number | null;
  category: string;
}

export interface PipelineMetrics {
  total_opportunities: number;
  active_opportunities: number;
  total_pipeline_value: number;
  weighted_pipeline_value: number;
  won_count: number;
  won_value: number;
  lost_count: number;
  monthly_recurring_value: number;
  one_time_pipeline_value: number;
}

export interface StageMetrics {
  stage_id: string;
  stage_name: string;
  stage_description: string | null;
  sort_order: number;
  color: string;
  stage_probability: number;
  is_won: boolean;
  is_lost: boolean;
  opportunity_count: number;
  total_value: number;
  weighted_value: number;
}

export interface PipelineActivity {
  id: string;
  opportunity_id: string;
  activity_type: string;
  title: string;
  description: string | null;
  activity_date: string;
  duration_minutes: number | null;
  performed_by_id: string | null;
  from_stage_id: string | null;
  to_stage_id: string | null;
  follow_up_date: string | null;
  follow_up_completed: boolean;
}

// ============= QUERY HOOKS =============

export function usePipelineMetrics() {
  return useQuery({
    queryKey: ['pipeline-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_metrics' as any)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as PipelineMetrics;
    },
    refetchInterval: 30000,
  });
}

export function usePipelineStages() {
  return useQuery({
    queryKey: ['pipeline-stages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_stages' as any)
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return (data || []) as unknown as PipelineStage[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePipelineProducts() {
  return useQuery({
    queryKey: ['pipeline-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_products' as any)
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data || []) as unknown as PipelineProduct[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePipelineBoard() {
  return useQuery({
    queryKey: ['pipeline-board'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_board' as any)
        .select('*');
      if (error) throw error;
      return (data || []) as unknown as PipelineOpportunity[];
    },
    refetchInterval: 15000,
  });
}

export function usePipelineByStage() {
  return useQuery({
    queryKey: ['pipeline-by-stage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_by_stage' as any)
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return (data || []) as unknown as StageMetrics[];
    },
    refetchInterval: 30000,
  });
}

export function useOpportunityActivities(opportunityId: string | null) {
  return useQuery({
    queryKey: ['pipeline-activities', opportunityId],
    queryFn: async () => {
      if (!opportunityId) return [];
      const { data, error } = await supabase
        .from('pipeline_activities' as any)
        .select('*')
        .eq('opportunity_id', opportunityId)
        .order('activity_date', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as PipelineActivity[];
    },
    enabled: !!opportunityId,
  });
}

export function usePipelinePersonas() {
  return useQuery({
    queryKey: ['pipeline-personas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sending_personas')
        .select('id, full_name, email, title, team')
        .eq('is_active', true)
        .order('full_name');
      if (error) throw error;
      return (data || []) as { id: string; full_name: string; email: string | null; team: string | null; title: string | null }[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ============= MUTATION HOOKS =============

export function useUpdateOpportunityStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ opportunityId, stageId }: { opportunityId: string; stageId: string }) => {
      // Get current stage for activity log
      const { data: currentData } = await supabase
        .from('pipeline_opportunities' as any)
        .select('stage_id')
        .eq('id', opportunityId)
        .single();
      const current = currentData as any;

      const { error } = await supabase
        .from('pipeline_opportunities' as any)
        .update({ stage_id: stageId, updated_at: new Date().toISOString() })
        .eq('id', opportunityId);
      if (error) throw error;

      // Log stage change activity
      if (current?.stage_id !== stageId) {
        await supabase.from('pipeline_activities' as any).insert({
          opportunity_id: opportunityId,
          activity_type: 'stage_change',
          title: 'Stage changed',
          from_stage_id: current?.stage_id || null,
          to_stage_id: stageId,
          activity_date: new Date().toISOString(),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-by-stage'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-activities'] });
    },
  });
}

export function useCreateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      stage_id: string;
      product_id?: string | null;
      deal_value?: number | null;
      owner_id?: string | null;
      expected_close_date?: string | null;
      next_action?: string | null;
      next_action_date?: string | null;
      notes?: string | null;
      company_domain?: string | null;
      account_id?: string | null;
      main_contact_name?: string | null;
      main_contact_email?: string | null;
      main_contact_linkedin?: string | null;
      revenue_regularity?: string | null;
      opp_summary?: string | null;
      single_sales_objective?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('pipeline_opportunities' as any)
        .insert({
          name: input.name,
          stage_id: input.stage_id,
          product_id: input.product_id || null,
          deal_value: input.deal_value || null,
          owner_id: input.owner_id || null,
          expected_close_date: input.expected_close_date || null,
          next_action: input.next_action || null,
          next_action_date: input.next_action_date || null,
          notes: input.notes || null,
          company_domain: input.company_domain || null,
          account_id: input.account_id || null,
          main_contact_name: input.main_contact_name || null,
          main_contact_email: input.main_contact_email || null,
          main_contact_linkedin: input.main_contact_linkedin || null,
          revenue_regularity: input.revenue_regularity || null,
          opp_summary: input.opp_summary || null,
          single_sales_objective: input.single_sales_objective || null,
          is_active: true,
        })
        .select('id')
        .single();
      if (error) throw error;
      return (data as any)?.id as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-by-stage'] });
    },
  });
}

export function useUpdateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      stage_id?: string;
      product_id?: string | null;
      deal_value?: number | null;
      owner_id?: string | null;
      expected_close_date?: string | null;
      next_action?: string | null;
      next_action_date?: string | null;
      notes?: string | null;
      company_domain?: string | null;
      account_id?: string | null;
      main_contact_name?: string | null;
      main_contact_email?: string | null;
      main_contact_linkedin?: string | null;
      revenue_regularity?: string | null;
      opp_summary?: string | null;
      single_sales_objective?: string | null;
    }) => {
      const { id, ...updates } = input;
      const { error } = await supabase
        .from('pipeline_opportunities' as any)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-by-stage'] });
    },
  });
}

export function useAddActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      opportunity_id: string;
      activity_type: string;
      title: string;
      description?: string | null;
      activity_date: string;
    }) => {
      const { error } = await supabase
        .from('pipeline_activities' as any)
        .insert(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-activities'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
    },
  });
}

export function useArchiveOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (opportunityId: string) => {
      const { error } = await supabase
        .from('pipeline_opportunities' as any)
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', opportunityId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-by-stage'] });
    },
  });
}
